/**
 * QuizRunner — Dynamic quiz step renderer
 * 
 * Reads current step from registry and renders appropriate component.
 * Handles navigation, loading states, and error boundaries.
 */

import { Suspense, useEffect, useMemo } from 'react';
import { useSafeNavigate } from '@/hooks/useSafeNavigate';
import { useSafeLocation } from '@/hooks/useSafeLocation';
import { getStepByRoute, getStepById, QuizStepState, QUIZ_STEPS_REGISTRY } from '@/lib/quiz/quizStepsRegistry';
import { useQuiz } from '@/contexts/QuizContext';
import { analyticsService } from '@/lib/services/analyticsService';

/** Loading skeleton for quiz screens */
function QuizLoadingFallback() {
  return (
    <div className="min-h-screen min-h-[100dvh] flex flex-col bg-bg-primary">
      <div 
        className="w-full flex-1 flex flex-col items-center justify-center"
        style={{ maxWidth: '420px', margin: '0 auto' }}
      >
        <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    </div>
  );
}

/** Error fallback for quiz screens */
function QuizErrorFallback({ error }: { error: Error }) {
  const navigate = useSafeNavigate();
  
  return (
    <div className="min-h-screen min-h-[100dvh] flex flex-col bg-bg-primary">
      <div 
        className="w-full flex-1 flex flex-col items-center justify-center px-4 text-center"
        style={{ maxWidth: '420px', margin: '0 auto' }}
      >
        <p className="text-text-primary mb-4">Что-то пошло не так</p>
        <button
          onClick={() => navigate('/quiz')}
          className="px-6 py-3 bg-bg-button-primary text-btn-primary-foreground rounded-xl"
        >
          Вернуться к началу
        </button>
        {process.env.NODE_ENV === 'development' && (
          <pre className="mt-4 text-xs text-text-tertiary overflow-auto max-w-full">
            {error.message}
          </pre>
        )}
      </div>
    </div>
  );
}

interface QuizRunnerProps {
  /** Override current route (for testing) */
  overrideRoute?: string;
}

/**
 * QuizRunner Component
 * 
 * Main controller for quiz flow.
 * Reads route, finds step in registry, renders component.
 */
export function QuizRunner({ overrideRoute }: QuizRunnerProps) {
  const location = useSafeLocation();
  const navigate = useSafeNavigate();
  const { state: quizState, goToStep, setProcessingStatus } = useQuiz();
  
  // Determine current route
  const currentRoute = overrideRoute || location.pathname;
  
  // Find current step definition
  const currentStep = useMemo(() => {
    return getStepByRoute(currentRoute);
  }, [currentRoute]);
  
  // Build state for step functions
  const stepState: QuizStepState = useMemo(() => ({
    currentStep: quizState.currentStep,
    answers: quizState.answers,
    petType: quizState.answers[0] as 'dog' | 'cat' | undefined,
    processingStatus: quizState.processingStatus,
  }), [quizState]);
  
  // Track step view
  useEffect(() => {
    if (currentStep) {
      analyticsService.track('quiz_step_view', {
        screen_id: currentStep.id,
        step_index: currentStep.stepIndex,
        category: currentStep.category,
      }, currentStep.id);
      
      // Call onEnter if defined
      currentStep.onEnter?.(stepState);
    }
  }, [currentStep?.id]);
  
  // Handle unknown routes
  if (!currentStep) {
    return (
      <div className="min-h-screen min-h-[100dvh] flex flex-col bg-bg-primary">
        <div 
          className="w-full flex-1 flex flex-col items-center justify-center px-4 text-center"
          style={{ maxWidth: '420px', margin: '0 auto' }}
        >
          <p className="text-text-primary mb-4">Шаг не найден</p>
          <button
            onClick={() => navigate('/quiz')}
            className="px-6 py-3 bg-bg-button-primary text-btn-primary-foreground rounded-xl"
          >
            Вернуться к началу
          </button>
        </div>
      </div>
    );
  }
  
  const StepComponent = currentStep.component;
  
  return (
    <Suspense fallback={<QuizLoadingFallback />}>
      <StepComponent />
    </Suspense>
  );
}

// Re-export useQuizNavigation from dedicated hook for backwards compatibility
export { useQuizNavigation } from '@/hooks/useQuizNavigation';

export default QuizRunner;
